import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  isChildItemActive,
  type SidebarNavFeature,
} from "@/config/sidebar-nav";
import { cn } from "@/lib/utils";

type Props = {
  feature: SidebarNavFeature;
  className?: string;
};

/**
 * The tab strip of a tabbed Coach page (Teams, Performance). Tabs are the
 * feature's visible `children`; the URL is the only state, so each trigger is
 * a router link and no `TabsContent` is rendered — the route outlet is the
 * body. The index tab owns every deeper route no sibling claims, so it stays
 * lit on `/coach/teams/:teamId`.
 */
export function CoachSectionTabs({ feature, className }: Props) {
  const { pathname } = useLocation();
  const { t } = useTranslation("nav");
  const tabs = feature.children ?? [];
  if (tabs.length < 2) return null;

  const siblingUrls = tabs.map((c) => c.url);
  const active =
    tabs.find((c) => isChildItemActive(pathname, c.url, siblingUrls))?.url ??
    feature.url;

  return (
    <Tabs value={active} activationMode="manual" className={className}>
      <TabsList aria-label={t(feature.titleKey)}>
        {tabs.map((c) => (
          <TabsTrigger key={c.url} value={c.url} asChild>
            <Link
              to={c.url}
              className={cn(c.url === active && "pointer-events-none")}
            >
              {t(c.titleKey)}
            </Link>
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}
